import { getTranslate } from 'legacy-src/utilities/i18n'
import Head from 'next/head'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { addGtmProductEvent } from '@/legacy-src/analytics/gtm'
import ErrorComponentGeneral from '@/legacy-src/components/ErrorComponentGeneral'
import { useCanonicalUrl } from '@/legacy-src/hooks/useCanonicalUrl'
import Container from '@/legacy-src/rebrand/Container'
import { type ServiceType } from '@/legacy-src/types/GeneralStateType'
import { type TherapistPriceType, type TherapistType } from '@/legacy-src/types/TherapistStateType'

import { TherapistCartProvider } from '@/src/domains/cart/components/TherapistCartProvider'

import ClientCalendarMultibooking from '../components/calendars/client/multibooking/ClientCalendarMultibooking'
import Loader from '../components/Loader'
import NotAcceptingNewBanner from '../components/profiles/NotAcceptingNewBanner'
import TherapistProfileHeader from '../components/profiles/TherapistProfileHeader'
import TherapistBio from '../components/TherapistBio'
import TherapistCalendarBenefits from '../components/TherapistCalendarBenefits'
import TherapistFreeSessionAd from '../components/TherapistFreeSessionAd'
import useFreeSession from '../hooks/useFreeSession'
import useServices from '../hooks/useServices'
import { LayoutPublicPages } from '../layouts/LayoutPublicPages'
import ClientActions from '../redux/ClientRedux'
import { USER_TYPE } from '../redux/UserRedux'
import { getFullName } from '../utilities/name'

type Props = { slug: string; therapist: TherapistType }

const PublicTherapistProfileContainer: React.FC<Props> = ({ therapist }) => {
  const dispatch = useDispatch()
  const { allowBookingForFree } = useFreeSession()
  const { t } = getTranslate()
  const { queryServiceId, services } = useServices()

  const therapistId = therapist?.therapistId
  const selectedServiceId =
    queryServiceId ?? therapist?.services?.find((serviceId) => services?.some(({ id }) => id === serviceId))
  const selectedService = services?.find((service: ServiceType) => service.id === selectedServiceId)

  const price = therapist?.prices?.find((price: TherapistPriceType) => price.serviceId === selectedServiceId)?.price
  const { firstName, lastName, titleAfter, titleBefore, image, therapistUserId, description } = therapist || {}
  const isAcceptingNewClients = therapist.visible && therapist.freeSchedulesCountLongTerm > 0
  const publicUrl = useCanonicalUrl()

  useEffect(() => {
    addGtmProductEvent(
      'terapeut_detail',
      'detail',
      {
        ...therapist,
        price,
        service: {
          ...selectedService,
          // TODO: remove this after checking with analytics
          // price above was broken before and the analytics may depend on it being here
          price,
        },
      },
      true,
    )
  }, [])

  useEffect(() => {
    if (therapistId && therapistUserId) {
      dispatch(ClientActions.getTherapistReviewsRequest(therapist.id, therapistUserId, USER_TYPE.CLIENT))
    }
  }, [therapistId, therapistUserId])

  if (!services || price == null || !selectedService || !therapist.services) return <ErrorComponentGeneral />

  const fullName = getFullName({ person: { firstName, lastName }, titleAfter, titleBefore })
  const metaDescription = t('meta.therapistMoto', { moto: description, name: fullName })

  return (
    <>
      <Head>
        <title>
          {firstName || lastName
            ? t('headers.openTherapist', {
                therapist: fullName,
              })
            : t('headers.therapistProfile')}
        </title>
        <link href={publicUrl} />
        <link rel="canonical" href={publicUrl} />
        <meta content={metaDescription} name="description" />
        <meta
          content={t('headers.openTherapist', {
            therapist: fullName,
          })}
          name="og:title"
          property="og:title"
        />
        <meta content={metaDescription} name="og:description" property="og:description" />
        <meta content={publicUrl} property="og:url" />
        <meta content={image} property="og:image" />
      </Head>
      <LayoutPublicPages>
        <div className="relative mt-6 min-h-screen bg-body-bg">
          <div className="relative">
            <Container>
              <TherapistProfileHeader price={price} therapistId={therapistId} />
              {therapistId &&
                (isAcceptingNewClients ? (
                  <>
                    {therapist && selectedService ? (
                      <TherapistCartProvider therapist={therapist} service={selectedService}>
                        <ClientCalendarMultibooking therapist={therapist} service={selectedService} />
                      </TherapistCartProvider>
                    ) : (
                      <Loader visible loaderType="section-large" />
                    )}
                    <TherapistCalendarBenefits />
                  </>
                ) : (
                  <NotAcceptingNewBanner />
                ))}
              <TherapistBio therapistId={therapistId} userType={USER_TYPE.CLIENT} />
              {allowBookingForFree && <TherapistFreeSessionAd price={price} sessionLength={selectedService.duration} />}
            </Container>
          </div>
        </div>
      </LayoutPublicPages>
    </>
  )
}

export default PublicTherapistProfileContainer
